import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@feathr/components';

import KeywordPill from '../AdGroupEditor/KeywordPill';
import type { IAdGroupKeyword } from '../AdStep.types';

interface IAdGroupKeywordsProps {
  keywords: IAdGroupKeyword[];
  /**
   * Messages for every keyword that has any, keyed by keyword key. Omit where there is
   * nothing to flag — the Review step renders the same list without validating it.
   */
  keywordErrors?: Record<string, string[]>;
  /** Match type is shown for positive keywords only; negative keywords have none. */
  showMatchType?: boolean;
}

/**
 * A read-only chip list of an ad group's keywords, for the collapsed summary.
 *
 * @param props - {@link IAdGroupKeywordsProps}.
 */
function AdGroupKeywords({
  keywords,
  keywordErrors = {},
  showMatchType = false,
}: Readonly<IAdGroupKeywordsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  if (!keywords.length) {
    return <Text.SmallRegular>{t('None yet.')}</Text.SmallRegular>;
  }

  return (
    <Flex gap={theme.spacing.half} wrap={'wrap'}>
      {keywords.map((keyword) => (
        <KeywordPill
          // Kept red here too, so a problem shows without expanding the ad group.
          hasError={!!keywordErrors[keyword.key]?.length}
          key={keyword.key}
          matchType={showMatchType ? keyword.matchType : undefined}
          text={keyword.text}
        />
      ))}
    </Flex>
  );
}

export default AdGroupKeywords;
